import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiCheckCircle, FiHome, FiPackage } from 'react-icons/fi';
import { supabase } from '../config/supabase';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: #5efba9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const StyledCheckCircle = styled(FiCheckCircle)`
  width: 48px;
  height: 48px;
  color: white;
`;

const StyledPackage = styled(FiPackage)`
  width: 48px;
  height: 48px;
  color: white;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
  text-align: center;
`;

const Message = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 8px;
  text-align: center;
  max-width: 600px;
`;

const OrderNumber = styled.p`
  font-size: 16px;
  color: #374151;
  margin-bottom: 32px;
  font-weight: 500;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 32px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
`;

const PrimaryButton = styled(Button)`
  background: #5efba9;
  color: #374151;

  &:hover {
    background: #4ce89a;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f3f4f6;
  color: #374151;

  &:hover {
    background: #e5e7eb;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            order_items:order_items(*)
          `)
          .eq('id', orderId)
          .single();

        if (error) throw error;

        setOrder(data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <FiPackage size={48} style={{ margin: '0 auto 16px', display: 'block' }} />
          <p>Loading order details...</p>
        </LoadingContainer>
      </Container>
    );
  }

  const isPaid = order?.payment_status === 'paid';

  return (
    <Container>
      <Card>
        <IconWrapper>
          {isPaid ? <StyledCheckCircle /> : <StyledPackage />}
        </IconWrapper>
        
        <Title>{isPaid ? 'Order Successful!' : 'Payment Pending'}</Title>
        <Message>
          {isPaid
            ? 'Thank you for your purchase. Your order has been placed successfully.'
            : 'Your order was created but payment was not completed yet. Please complete payment to confirm your order.'}
        </Message>
        
        {order && (
          <OrderNumber>Order ID: {order.id.substring(0, 8).toUpperCase()}</OrderNumber>
        )}

        <ButtonGroup>
          {isPaid ? (
            <PrimaryButton onClick={() => navigate('/')}>
              <FiHome /> Continue Shopping
            </PrimaryButton>
          ) : (
            <>
              <PrimaryButton onClick={() => navigate('/checkout')}>
                Complete Payment
              </PrimaryButton>
              <SecondaryButton onClick={() => navigate('/')}>
                <FiHome /> Back Home
              </SecondaryButton>
            </>
          )}
        </ButtonGroup>
      </Card>
    </Container>
  );
}

export default OrderConfirmation;
